import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, User, Package, DollarSign, Tag, Loader2, Send,
  MessageSquare, Mail, Phone, Star, ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { MediaUpload } from "../components/MediaUpload";
import { MediaGallery } from "../components/MediaGallery";
import { RatingSummary, RatingStars, RatingPanel } from "../components/Rating";
import { SaveButton } from "../components/SaveButton";
import { ShareButton } from "../components/ShareButton";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { toast } from "sonner";

export default function RequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [req, setReq] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [offer, setOffer] = useState({
    seller_name: "",
    price: "",
    availability: "",
    description: "",
    contact_info: "",
    email: "",
    phone: "",
    media: [],
  });

  const load = async () => {
    try {
      const [r, o] = await Promise.all([
        api.get(`/requests/${id}`),
        api.get(`/requests/${id}/offers`),
      ]);
      setReq(r.data);
      setOffers(o.data);
    } catch {
      toast.error("Request not found");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const setF = (k, v) => setOffer((f) => ({ ...f, [k]: v }));

  const submitOffer = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post(`/requests/${id}/offers`, offer);
      setOffers([res.data, ...offers]);
      setOffer({
        seller_name: "", price: "", availability: "", description: "",
        contact_info: "", email: "", phone: "", media: [],
      });
      setShowForm(false);
      toast.success(t("offer_posted"));
    } catch {
      toast.error(t("error_submit"));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmitOffer =
    offer.seller_name.trim() &&
    offer.price.trim() &&
    offer.availability.trim() &&
    offer.description.trim() &&
    offer.contact_info.trim() &&
    !submitting;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!req) return null;

  return (
    <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-6 sm:py-10 md:py-14">
      <button
        data-testid="back-to-board"
        onClick={() => navigate("/")}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6 sm:mb-8"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("back_to_board")}
      </button>

      {/* Hero grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-12 mb-10 lg:mb-12">
        <div className="lg:col-span-5">
          <div className="relative">
            <div className="absolute top-4 left-4 z-10 flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-[10px] tracking-[0.15em] uppercase font-bold border border-border">
              <Tag className="h-3 w-3" strokeWidth={2.5} />
              {req.category}
            </div>
            <MediaGallery media={req.media} fallbackPath={req.photo_path} />
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="flex items-center gap-2 mb-4">
            <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">
              <span className="h-2 w-2 rounded-full bg-accent"></span>
              {t("live")} · buy request
            </div>
          </div>
          <h1
            data-testid="request-title"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] break-words"
          >
            {req.title}
          </h1>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <SaveButton targetType="request" targetId={req.id} variant="button" stopPropagation={false} />
            <ShareButton
              targetType="request"
              targetId={req.id}
              title={req.title}
              description={req.description}
              variant="button"
              stopPropagation={false}
            />
          </div>
          {req.rating_count > 0 && (
            <div className="mt-4">
              <RatingSummary avg={req.rating_avg || 0} count={req.rating_count || 0} size={5} />
            </div>
          )}
          <p className="mt-6 text-base text-foreground/80 leading-relaxed whitespace-pre-wrap">
            {req.description}
          </p>

          <div className="mt-10 grid grid-cols-2 gap-6 pt-8 border-t border-border">
            <StatBlock
              icon={<Package className="h-4 w-4" />}
              label={t("quantity")}
              value={req.quantity.toLocaleString()}
            />
            <StatBlock
              icon={<DollarSign className="h-4 w-4" />}
              label={t("budget")}
              value={req.budget}
            />
            <StatBlock
              icon={<User className="h-4 w-4" />}
              label={t("request_by")}
              value={
                <button
                  onClick={() => navigate(`/s/${encodeURIComponent(req.buyer_name)}`)}
                  className="hover:text-primary transition-colors text-left"
                  data-testid="request-buyer-link"
                >
                  {req.buyer_name}
                </button>
              }
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="offers" className="w-full">
        <TabsList className="bg-white border border-border rounded-md p-1 h-auto w-full sm:w-auto flex-nowrap overflow-x-auto scrollbar-none justify-start">
          <TabsTrigger data-testid="tab-offers" value="offers" className="rounded-sm data-[state=active]:bg-foreground data-[state=active]:text-background gap-2 px-4 py-2">
            <MessageSquare className="h-3.5 w-3.5" />
            {t("offers_tab")}
            <span className="font-mono-brand text-[10px] opacity-70">{offers.length}</span>
          </TabsTrigger>
          <TabsTrigger data-testid="tab-contact" value="contact" className="rounded-sm data-[state=active]:bg-foreground data-[state=active]:text-background gap-2 px-4 py-2">
            <Mail className="h-3.5 w-3.5" />
            {t("contact_tab")}
          </TabsTrigger>
          <TabsTrigger data-testid="tab-ratings" value="ratings" className="rounded-sm data-[state=active]:bg-foreground data-[state=active]:text-background gap-2 px-4 py-2">
            <Star className="h-3.5 w-3.5" />
            {t("ratings_tab")}
            <span className="font-mono-brand text-[10px] opacity-70">{req.rating_count || 0}</span>
          </TabsTrigger>
        </TabsList>

        {/* Offers tab */}
        <TabsContent value="offers" className="mt-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-8">
              {offers.length === 0 ? (
                <div
                  data-testid="no-offers"
                  className="rounded-md border-2 border-dashed border-border bg-white p-10 text-center"
                >
                  <MessageSquare className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" strokeWidth={1.5} />
                  <p className="text-sm text-muted-foreground">{t("no_offers")}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {offers.map((o, i) => (
                    <OfferCard key={o.id} offer={o} index={i} onRefresh={load} />
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                {!showForm ? (
                  <div className="rounded-md border border-border bg-foreground text-background p-6 md:p-8 relative overflow-hidden">
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold opacity-60 mb-3">
                      Are you a supplier?
                    </div>
                    <h3 className="font-display text-2xl font-semibold leading-tight mb-2">
                      Can you deliver this?
                    </h3>
                    <p className="text-sm opacity-70 mb-6 leading-relaxed">
                      Post your price, availability, and contact info. The buyer sees it instantly.
                    </p>
                    <Button
                      data-testid="show-offer-form-btn"
                      onClick={() => setShowForm(true)}
                      variant="secondary"
                      size="lg"
                      className="w-full rounded-md h-12 bg-background text-foreground hover:bg-background/90"
                    >
                      {t("add_offer")}
                    </Button>
                    <div className="absolute -bottom-8 -right-8 h-32 w-32 rounded-full bg-accent/30 blur-3xl"></div>
                  </div>
                ) : (
                  <form
                    onSubmit={submitOffer}
                    className="rounded-md border border-border bg-white p-6 space-y-5"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-display text-lg font-semibold">{t("add_offer")}</h3>
                      <button
                        type="button"
                        onClick={() => setShowForm(false)}
                        className="text-xs text-muted-foreground hover:text-foreground"
                        data-testid="cancel-offer-btn"
                      >
                        Cancel
                      </button>
                    </div>

                    <FieldRow label={t("seller_name")}>
                      <Input
                        data-testid="offer-seller-name"
                        value={offer.seller_name}
                        onChange={(e) => setF("seller_name", e.target.value)}
                        placeholder={t("seller_name_placeholder")}
                        className="rounded-md h-10"
                        required
                      />
                    </FieldRow>
                    <FieldRow label={t("price_label")}>
                      <Input
                        data-testid="offer-price"
                        value={offer.price}
                        onChange={(e) => setF("price", e.target.value)}
                        placeholder={t("price_placeholder")}
                        className="rounded-md h-10 font-mono-brand"
                        required
                      />
                    </FieldRow>
                    <FieldRow label={t("availability_label")}>
                      <Input
                        data-testid="offer-availability"
                        value={offer.availability}
                        onChange={(e) => setF("availability", e.target.value)}
                        placeholder={t("availability_placeholder")}
                        className="rounded-md h-10"
                        required
                      />
                    </FieldRow>
                    <FieldRow label={t("offer_description_label")}>
                      <Textarea
                        data-testid="offer-description"
                        value={offer.description}
                        onChange={(e) => setF("description", e.target.value)}
                        placeholder={t("offer_description_placeholder")}
                        rows={3}
                        className="rounded-md resize-none"
                        required
                      />
                    </FieldRow>
                    <FieldRow label={t("contact_label")}>
                      <Input
                        data-testid="offer-contact"
                        value={offer.contact_info}
                        onChange={(e) => setF("contact_info", e.target.value)}
                        placeholder={t("contact_placeholder")}
                        className="rounded-md h-10"
                        required
                      />
                    </FieldRow>
                    <div className="grid grid-cols-2 gap-3">
                      <FieldRow label={t("email_label")}>
                        <Input
                          data-testid="offer-email"
                          type="email"
                          value={offer.email}
                          onChange={(e) => setF("email", e.target.value)}
                          placeholder={t("email_placeholder")}
                          className="rounded-md h-10 text-xs"
                        />
                      </FieldRow>
                      <FieldRow label={t("phone_label")}>
                        <Input
                          data-testid="offer-phone"
                          value={offer.phone}
                          onChange={(e) => setF("phone", e.target.value)}
                          placeholder={t("phone_placeholder")}
                          className="rounded-md h-10 text-xs font-mono-brand"
                        />
                      </FieldRow>
                    </div>
                    <FieldRow label={t("photo_label")}>
                      <MediaUpload
                        media={offer.media}
                        onChange={(m) => setF("media", m)}
                        testId="offer-media-upload"
                      />
                    </FieldRow>

                    <Button
                      type="submit"
                      data-testid="submit-offer-btn"
                      disabled={!canSubmitOffer}
                      className="w-full rounded-md h-11 gap-2"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> {t("posting")}
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" /> {t("submit_offer")}
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Contact tab */}
        <TabsContent value="contact" className="mt-8">
          <div className="max-w-2xl">
            <div className="rounded-md border border-border bg-white p-8 space-y-5" data-testid="contact-panel">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">
                {t("contact_tab")} — {req.buyer_name}
              </div>
              <ContactRow
                icon={<Mail className="h-4 w-4" />}
                label={t("email_label")}
                value={req.email}
                href={req.email ? `mailto:${req.email}` : null}
                testId="buyer-contact-email"
              />
              <ContactRow
                icon={<Phone className="h-4 w-4" />}
                label={t("phone_label")}
                value={req.phone}
                href={req.phone ? `tel:${req.phone.replace(/\s+/g, "")}` : null}
                testId="buyer-contact-phone"
              />
              {!req.email && !req.phone && (
                <p className="text-sm text-muted-foreground">
                  Buyer has not shared direct contact details. Reply in the Offers tab instead.
                </p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Ratings tab */}
        <TabsContent value="ratings" className="mt-8">
          <div className="max-w-2xl">
            <div className="mb-6">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2">
                Rate this buyer
              </div>
              <RatingSummary avg={req.rating_avg || 0} count={req.rating_count || 0} size={5} />
            </div>
            <RatingPanel targetType="request" targetId={req.id} onRated={load} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatBlock({ icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-muted-foreground mb-1.5">
        {icon}
        <span className="text-[10px] tracking-[0.15em] uppercase font-semibold">
          {label}
        </span>
      </div>
      <div className="font-display text-xl md:text-2xl font-semibold">{value}</div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

function ContactRow({ icon, label, value, href, testId }) {
  if (!value) {
    return (
      <div className="flex items-center gap-3 py-2 border-b border-border last:border-0">
        <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center text-muted-foreground">
          {icon}
        </div>
        <div>
          <div className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
            {label}
          </div>
          <div className="text-sm text-muted-foreground">Not provided</div>
        </div>
      </div>
    );
  }
  const Comp = href ? "a" : "div";
  return (
    <Comp
      href={href}
      data-testid={testId}
      className="flex items-center gap-3 py-3 border-b border-border last:border-0 hover:bg-secondary/50 -mx-2 px-2 rounded-md transition-colors"
    >
      <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
          {label}
        </div>
        <div className="font-mono-brand text-sm font-medium truncate">{value}</div>
      </div>
    </Comp>
  );
}

function OfferCard({ offer, index, onRefresh }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [showRatings, setShowRatings] = useState(false);
  const media =
    offer.media && offer.media.length > 0
      ? offer.media
      : offer.photo_path
      ? [{ path: offer.photo_path, type: "image" }]
      : [];

  return (
    <article
      data-testid={`offer-card-${offer.id}`}
      style={{ animationDelay: `${index * 60}ms` }}
      className="fade-up rounded-md border border-border bg-white p-5 md:p-6 hover:border-foreground/20 transition-colors"
    >
      <div className="flex flex-col md:flex-row md:items-start gap-5">
        {media.length > 0 && (
          <div className="md:w-48 md:flex-shrink-0">
            <MediaGallery media={media} aspect="aspect-square" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1">
                supplier
              </div>
              <button
                onClick={() => navigate(`/s/${encodeURIComponent(offer.seller_name)}`)}
                className="font-display text-lg font-semibold hover:text-primary transition-colors text-left"
                data-testid={`offer-seller-link-${offer.id}`}
              >
                {offer.seller_name}
              </button>
              {offer.rating_count > 0 && (
                <div className="mt-1.5">
                  <RatingSummary avg={offer.rating_avg || 0} count={offer.rating_count || 0} size={3} />
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1">
                Price
              </div>
              <div className="font-mono-brand text-xl font-bold text-primary">
                {offer.price}
              </div>
            </div>
          </div>

          <div className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-semibold mb-3">
            <span className="h-1.5 w-1.5 rounded-full bg-accent"></span>
            {offer.availability}
          </div>

          <p className="text-sm text-foreground/80 leading-relaxed mb-4 whitespace-pre-wrap">
            {offer.description}
          </p>

          <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-border text-xs">
            <div className="flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-mono-brand font-medium">{offer.email || offer.contact_info}</span>
            </div>
            {offer.phone && (
              <div className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                <a
                  href={`tel:${offer.phone.replace(/\s+/g, "")}`}
                  className="font-mono-brand font-medium hover:text-primary"
                >
                  {offer.phone}
                </a>
              </div>
            )}
          </div>

          <button
            data-testid={`toggle-offer-ratings-${offer.id}`}
            onClick={() => setShowRatings(!showRatings)}
            className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            <Star className="h-3.5 w-3.5" />
            {showRatings ? "Hide ratings" : `Rate this supplier${offer.rating_count > 0 ? ` (${offer.rating_count})` : ""}`}
            {showRatings ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>

          {showRatings && (
            <div className="mt-4 pt-4 border-t border-border">
              <RatingPanel targetType="offer" targetId={offer.id} onRated={onRefresh} />
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
